import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RotateCcw, HelpCircle, ArrowLeft } from "lucide-react";

const CrossSumsGame = () => {
  const navigate = useNavigate();
  const [targetSums, setTargetSums] = useState({ rows: [], cols: [] });
  const [grid, setGrid] = useState(Array(9).fill(""));
  const [message, setMessage] = useState("");
  const [isWon, setIsWon] = useState(false);
  const [solution, setSolution] = useState([]);
  const [availableNumbers, setAvailableNumbers] = useState(
    Array.from({ length: 9 }, (_, i) => i + 1)
  );

  // Initialize game
  useEffect(() => {
    startNewGame();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startNewGame = () => {
    // 1. Generate a random solution (permutation of 1-9)
    const nums = [1, 2, 3, 4, 5, 6, 7, 8, 9];
    for (let i = nums.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [nums[i], nums[j]] = [nums[j], nums[i]];
    }
    setSolution(nums);

    // 2. Calculate target sums
    const rows = [
      nums[0] + nums[1] + nums[2],
      nums[3] + nums[4] + nums[5],
      nums[6] + nums[7] + nums[8],
    ];
    const cols = [
      nums[0] + nums[3] + nums[6],
      nums[1] + nums[4] + nums[7],
      nums[2] + nums[5] + nums[8],
    ];
    setTargetSums({ rows, cols });

    // 3. Reset state
    setGrid(Array(9).fill(""));
    setMessage("");
    setIsWon(false);
  };

  const handleInputChange = (index, value) => {
    if (isWon) return;

    const val = parseInt(value);
    const newGrid = [...grid];

    if (!value) {
      newGrid[index] = "";
    } else if (val >= 1 && val <= 9) {
        // Basic duplicate check for immediate feedback (optional, maybe too easy?)
        // Let's just allow it and let them figure it out, or maybe just block non 1-9
        newGrid[index] = val;
    }

    setGrid(newGrid);
    checkWinCondition(newGrid);
  };

  const checkWinCondition = (currentGrid) => {
    // Check if full
    if (currentGrid.includes("")) {
      setMessage("");
      return;
    }

    // Check strict 1-9 usage (Set size 9)
    const uniqueNums = new Set(currentGrid);
    if (uniqueNums.size !== 9) {
      setMessage("Use each number 1-9 exactly once!");
      return;
    }

    // Check Row Sums
    const rows = [
      currentGrid[0] + currentGrid[1] + currentGrid[2],
      currentGrid[3] + currentGrid[4] + currentGrid[5],
      currentGrid[6] + currentGrid[7] + currentGrid[8],
    ];

    // Check Col Sums
    const cols = [
      currentGrid[0] + currentGrid[3] + currentGrid[6],
      currentGrid[1] + currentGrid[4] + currentGrid[7],
      currentGrid[2] + currentGrid[5] + currentGrid[8],
    ];

    const rowsMatch = rows.every((sum, i) => sum === targetSums.rows[i]);
    const colsMatch = cols.every((sum, i) => sum === targetSums.cols[i]);

    if (rowsMatch && colsMatch) {
      setMessage("Correct! 🎉");
      setIsWon(true);
    } else {
      setMessage("Sums don't match. Keep trying!");
    }
  };
  
  // Helper to check if a specific row/col is satisfied (for UI hints)
  const isRowComplete = (rIndex) => {
      const idx = rIndex * 3;
      if (grid[idx] === "" || grid[idx+1] === "" || grid[idx+2] === "") return false;
      return (grid[idx] + grid[idx+1] + grid[idx+2]) === targetSums.rows[rIndex];
  };

    const isColComplete = (cIndex) => {
      if (grid[cIndex] === "" || grid[cIndex+3] === "" || grid[cIndex+6] === "") return false;
      return (grid[cIndex] + grid[cIndex+3] + grid[cIndex+6]) === targetSums.cols[cIndex];
  };

  const handleShowSolution = () => {
      setGrid([...solution]);
      setIsWon(true); // Technically they didn't win, but stops input.
      setMessage("Here is the solution.");
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate("/games")}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            Back to Games
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Cross Sums 3x3</h1>
          <button
            onClick={startNewGame}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
            title="New Game"
          >
            <RotateCcw className="w-6 h-6" />
          </button>
        </div>

        {/* Instructions */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6 text-center">
            <p className="text-gray-600">
                Place numbers <span className="font-bold text-blue-600">1-9</span> into the grid exactly once so that rows and columns add up to the target numbers.
            </p>
        </div>

        {/* Game Board */}
        <div className="bg-white rounded-xl shadow-lg p-6 flex flex-col items-center">
          
          {/* Top Sums (Columns) */}
          <div className="grid grid-cols-4 gap-2 mb-2 w-full max-w-[300px]">
            <div className="w-12"></div> {/* Spacer for row sums column */}
            {targetSums.cols.map((sum, i) => (
               <div key={`col-target-${i}`} className={`flex items-center justify-center w-14 h-10 font-bold text-lg rounded ${isColComplete(i) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                 {sum}
               </div>
            ))}
          </div>

          <div className="grid grid-cols-4 gap-2 w-full max-w-[300px]">
             {/* Row 0 */}
             <div className={`flex items-center justify-center w-12 h-14 font-bold text-lg rounded mr-2 ${isRowComplete(0) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                 {targetSums.rows[0]}
             </div>
             {[0, 1, 2].map((idx) => (
                 <input
                    key={idx}
                    type="number"
                    value={grid[idx]}
                    onChange={(e) => handleInputChange(idx, e.target.value)}
                    className={`w-14 h-14 text-center text-2xl font-bold border-2 rounded-lg outline-none focus:border-blue-500 transition-all
                        ${grid[idx] && isWon ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-300 text-gray-800'}
                    `}
                 />
             ))}

             {/* Row 1 */}
             <div className={`flex items-center justify-center w-12 h-14 font-bold text-lg rounded mr-2 ${isRowComplete(1) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                 {targetSums.rows[1]}
             </div>
             {[3, 4, 5].map((idx) => (
                 <input
                    key={idx}
                    type="number"
                    value={grid[idx]}
                    onChange={(e) => handleInputChange(idx, e.target.value)}
                    className={`w-14 h-14 text-center text-2xl font-bold border-2 rounded-lg outline-none focus:border-blue-500 transition-all
                        ${grid[idx] && isWon ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-300 text-gray-800'}
                    `}
                 />
             ))}

             {/* Row 2 */}
             <div className={`flex items-center justify-center w-12 h-14 font-bold text-lg rounded mr-2 ${isRowComplete(2) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                 {targetSums.rows[2]}
             </div>
             {[6, 7, 8].map((idx) => (
                 <input
                    key={idx}
                    type="number"
                    value={grid[idx]}
                    onChange={(e) => handleInputChange(idx, e.target.value)}
                    className={`w-14 h-14 text-center text-2xl font-bold border-2 rounded-lg outline-none focus:border-blue-500 transition-all
                        ${grid[idx] && isWon ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-300 text-gray-800'}
                    `}
                 />
             ))}
          </div>

          <div className="mt-6 h-8 text-center font-bold text-lg">
              <span className={isWon ? "text-green-600" : "text-red-500"}>{message}</span>
          </div>

          <div className="mt-8 flex gap-4">
              <button
                onClick={handleShowSolution}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg flex items-center transition-colors"
              >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Show Solution
              </button>
              {isWon && (
                  <button
                    onClick={startNewGame}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-md transition-colors font-medium"
                  >
                      Play Again
                  </button>
              )}
          </div>
        </div>
        
        {/* Available Numbers Helper */}
        <div className="mt-8 max-w-[300px] mx-auto">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 text-center">Unused Numbers Helper</h3>
            <div className="flex justify-center flex-wrap gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => {
                    const isUsed = grid.includes(num);
                    return (
                        <div key={num}
                            className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold border transition-colors
                             ${isUsed ? 'bg-gray-100 text-gray-300 border-transparent' : 'bg-white text-gray-600 border-gray-300'}
                            `}
                        >
                            {num}
                        </div>
                    )
                })}
            </div>
        </div>

      </div>
    </div>
  );
};

export default CrossSumsGame;
